// Maps CEFR level keys to their CSS highlight classes
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../translations/LanguageContext";

const CEFR_CLASS = {
    a1: "ta-a1",
    a2: "ta-a2",
    b1: "ta-b1",
    b2plus: "ta-b2plus",
};

// CyTag tag prefixes mapped to POS keys. (Internal keys only — the
// displayed label comes from t("textAnnotator.pos.<key>").)
const POS_MAP = [
    // Verb nouns — must check before "B" (bare verb) and "E" standalone
    ["Egu",  "verbNoun"],
    ["Egll", "verbNoun"],
    ["Egb",  "verbNoun"],
    ["E",    "verbNoun"],
    // Verbs
    ["Bpres",  "verb"],
    ["Bgorff", "verb"],
    ["Bdyf",   "verb"],
    ["Bgorch", "verb"],
    ["B",      "verb"],

    // Others (specific before general)
    ["Adf",  "adverb"],
    ["Ans",  "adjective"],
    ["Ar",   "preposition"],
    ["Cys",  "conjunction"],
    ["Rha",  "pronoun"],
    ["YFB",  "article"],
    ["Ban",  "determiner"],
    ["Rhi",  "numeral"],
    ["U",    "particle"],
    ["Atd",  null],
    ["Gw",   null],
];

// POS types that are grammatical function words and not CEFR-graded.
// These get null so they render without a highlight.
const UNHIGHLIGHTED_POS = new Set([
    "article",
    "particle",
    "preposition",
    "conjunction",
    "pronoun",
]);

// Transforms the raw API response into a flat array of token objects
// that the renderer can consume directly.
function buildTokens(data) {
    const fullInstance = data?.instances?.["Full instance"];
    if (!fullInstance || fullInstance.length === 0) return null;

    const cefrInstances = data?.instances?.["CEFR instances"] ?? [];
    const stopInstances = data?.instances?.["Stopword instances"] ?? [];

    const cefrMap = {};
    cefrInstances.forEach(({ wordform, category }) => {
        if (!cefrMap[wordform]) {
            cefrMap[wordform] = category?.toLowerCase() ?? "b2plus";
        }
    });

    const stopSet = new Set(stopInstances.map(s => s.wordform));

    return fullInstance.map(token => {
        const [wordform, tag, lemma] = token;

        const primaryTag = tag?.split(" | ")[0].trim() ?? "";

        // POS
        const posMatch = POS_MAP.find(([prefix]) => prefix && primaryTag.startsWith(prefix));
        const pos = posMatch ? posMatch[1] : null;

        // Mutation
        let mutation = null;
        if (primaryTag.includes("SM"))      mutation = "soft";
        else if (primaryTag.includes("NM")) mutation = "nasal";
        else if (primaryTag.includes("AM")) mutation = "aspirate";

        const cefr = cefrMap[wordform] ?? (UNHIGHLIGHTED_POS.has(pos) ? null : "b2plus");

        return { text: wordform, lemma, tag: primaryTag, cefr, pos, mutation, stopword: stopSet.has(wordform) };
    });
}

// Renders a single annotated word with a hover tooltip.
function Word({ token }) {
    const { t } = useTranslation();
    const cefrCls = token.cefr ? (CEFR_CLASS[token.cefr] ?? "") : "";

    const cls = [
        "ta-word",
        cefrCls,
        token.mutation ? "ta-mutation" : "",
    ].filter(Boolean).join(" ");

    const cefrDisplay = token.cefr
        ? token.cefr.toUpperCase().replace("B2PLUS", "B2+")
        : "Unknown";

    const wordRef = useRef(null);
    const [tooltipPos, setTooltipPos] = useState(null);

    function handleMouseEnter() {
        const rect = wordRef.current.getBoundingClientRect();
        const estimatedTooltipHeight = 70; // rough; padding covers minor variance
        const openBelow = rect.top < estimatedTooltipHeight + 16;

        setTooltipPos({
            left: rect.left + rect.width / 2,
            top: openBelow ? rect.bottom + 8 : rect.top - 8,
            openBelow,
        });
    }

    function handleMouseLeave() {
        setTooltipPos(null);
    }

    // Close the tooltip on any scroll (container or page) so it never
    // renders in a stale spot relative to the word it describes.
    useEffect(() => {
        if (!tooltipPos) return;
        const close = () => setTooltipPos(null);
        window.addEventListener("scroll", close, true);
        return () => window.removeEventListener("scroll", close, true);
    }, [tooltipPos]);

    return (
        <span
            ref={wordRef}
            className={cls}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {token.text}
            {tooltipPos && (
                <span
                    className={`ta-tooltip${tooltipPos.openBelow ? " ta-tooltip--below" : ""}`}
                    role="tooltip"
                    style={{ left: tooltipPos.left, top: tooltipPos.top }}
                >
                    <span className="ta-tooltip-word">{token.text}</span>
                    <span className="ta-tooltip-row">{t("textAnnotator.tooltip.cefr", { value: cefrDisplay })}</span>
                    {token.pos && (
                        <span className="ta-tooltip-row">
                            {t(`textAnnotator.pos.${token.pos}`)}
                        </span>
                    )}
                    {token.mutation && (
                        <span className="ta-tooltip-row">
                            {t(`textAnnotator.mutation.${token.mutation}`)}
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}

// Renders the full annotated text view with a legend.
// `data` - should be the full API response object.
export function TextAnnotator({ data, setMode }) {
    const { t } = useTranslation();
    const tokens = buildTokens(data);
    if (!tokens) return null;

    return (
        <div className="ta-wrap">

            {/* Header: title + colour legend */}
            <div className="ta-header">
                <span className="ta-title">{t("textAnnotator.title")}</span>
                <div className="ta-legend">
                    <span className="ta-legend-item"><span className="ta-dot ta-a1" />{t("textAnnotator.legend.a1")}</span>
                    <span className="ta-legend-item"><span className="ta-dot ta-a2" />{t("textAnnotator.legend.a2")}</span>
                    <span className="ta-legend-item"><span className="ta-dot ta-b1" />{t("textAnnotator.legend.b1")}</span>
                    <span className="ta-legend-item"><span className="ta-dot ta-b2plus" />{t("textAnnotator.legend.b2plus")}</span>
                </div>
            </div>

            {/* Body: one <Word> per token, spaces inserted between */}
            <div className="ta-body">
                {tokens.map((token, i) =>
                    token.text === " " || token.text === "\n"
                        ? <span key={i}> </span>
                        : <Word key={i} token={token} />
                )}
            </div>

            <button className="ta-button" onClick={() => setMode("input")}>
                <span>&larr; {t("textAnnotator.editText")}</span>
            </button>
        </div>
    );
}
